import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback } from "react";
import {
  Save,
  Edit,
  Plus,
  Trash2,
  User,
  Image as ImageIcon,
} from "lucide-react";
import { motion } from "framer-motion";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import type { RootState } from "@/store";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ImageUpload from "@/components/common/ImageUpload";
import {
  useAboutAdmin,
  useUpdateAbout,
  AboutTranslation,
} from "@/services/aboutService";
import {
  useTeamMembers,
  useCreateTeamMember,
  useUpdateTeamMember,
  useDeleteTeamMember,
  TeamMember,
} from "@/services/teamMemberService";

interface AboutFormData {
  hero_title: string;
  hero_description: string;
  hero_image: string;
  story_title: string;
  story_content: string;
  mission_title: string;
  mission_content: string;
  vision_title: string;
  vision_content: string;
  contact_title: string;
  contact_description: string;
  contact_address: string;
  contact_email: string;
  contact_phone: string;
  contact_map_url: string;
  section_body: string;
}

const About: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  // Active tab state
  const [activeTab, setActiveTab] = useState("hero");

  // About data state
  const [aboutData, setAboutData] = useState<AboutFormData>({
    hero_title: "",
    hero_description: "",
    hero_image: "",
    story_title: "",
    story_content: "",
    mission_title: "",
    mission_content: "",
    vision_title: "",
    vision_content: "",
    contact_title: "",
    contact_description: "",
    contact_address: "",
    contact_email: "",
    contact_phone: "",
    contact_map_url: "",
    section_body: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isTeamDialogOpen, setIsTeamDialogOpen] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<TeamMember | null>(
    null
  );
  const [newTeamMember, setNewTeamMember] = useState({
    name: "",
    role: "",
    image_url: "",
  });

  // Team member image upload handler
  const handleTeamMemberImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          resolve([result]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    []
  );

  const handleNewTeamMemberImageChange = useCallback(
    (value: string | string[]) => {
      const imageUrl = Array.isArray(value) ? value[0] || "" : value || "";
      setNewTeamMember((prev) => ({ ...prev, image_url: imageUrl }));
    },
    []
  );

  const handleEditTeamMemberImageChange = useCallback(
    (value: string | string[]) => {
      const imageUrl = Array.isArray(value) ? value[0] || "" : value || "";
      if (editingTeamMember) {
        setEditingTeamMember({
          ...editingTeamMember,
          image_url: imageUrl,
        });
      }
    },
    [editingTeamMember]
  );

  // API hooks
  const { data: aboutResponse } = useAboutAdmin(1); // Assuming ID 1 for now
  const updateAboutMutation = useUpdateAbout();
  const { data: teamMembersResponse } = useTeamMembers();
  const createTeamMemberMutation = useCreateTeamMember();
  const updateTeamMemberMutation = useUpdateTeamMember();
  const deleteTeamMemberMutation = useDeleteTeamMember();

  // Load about data when available
  useEffect(() => {
    if (aboutResponse) {
      setAboutData({
        hero_title: aboutResponse.hero_title || "",
        hero_description: aboutResponse.hero_description || "",
        hero_image: aboutResponse.hero_image || "",
        story_title: aboutResponse.story_title || "",
        story_content: aboutResponse.story_content || "",
        mission_title: aboutResponse.mission_title || "",
        mission_content: aboutResponse.mission_content || "",
        vision_title: aboutResponse.vision_title || "",
        vision_content: aboutResponse.vision_content || "",
        contact_title: aboutResponse.contact_title || "",
        contact_description: aboutResponse.contact_description || "",
        contact_address: aboutResponse.contact_address || "",
        contact_email: aboutResponse.contact_email || "",
        contact_phone: aboutResponse.contact_phone || "",
        contact_map_url: aboutResponse.contact_map_url || "",
        section_body: aboutResponse.section_body || "",
      });
    }
  }, [aboutResponse]);

  // Hero image upload handler
  const handleHeroImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setAboutData((prev) => ({ ...prev, hero_image: result }));
          resolve([result]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    []
  );

  const handleHeroImageChange = useCallback((value: string | string[]) => {
    const imageUrl = Array.isArray(value) ? value[0] || "" : value || "";
    setAboutData((prev) => ({ ...prev, hero_image: imageUrl }));
  }, []);

  const handleSave = async () => {
    try {
      const translations: AboutTranslation[] = [
        {
          locale: currentLanguage,
          hero_title: aboutData.hero_title,
          hero_description: aboutData.hero_description,
          hero_image: aboutData.hero_image,
          story_title: aboutData.story_title,
          story_content: aboutData.story_content,
          mission_title: aboutData.mission_title,
          mission_content: aboutData.mission_content,
          vision_title: aboutData.vision_title,
          vision_content: aboutData.vision_content,
          contact_title: aboutData.contact_title,
          contact_description: aboutData.contact_description,
          contact_address: aboutData.contact_address,
          contact_email: aboutData.contact_email,
          contact_phone: aboutData.contact_phone,
          contact_map_url: aboutData.contact_map_url,
          section_body: aboutData.section_body,
        },
      ];

      await updateAboutMutation.mutateAsync({
        id: 1, // Assuming ID 1 for now
        aboutData: { translations },
      });

      setIsEditing(false);
    } catch (error) {
      console.error("Error saving about data:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original data
    if (aboutResponse) {
      setAboutData({
        hero_title: aboutResponse.hero_title || "",
        hero_description: aboutResponse.hero_description || "",
        hero_image: aboutResponse.hero_image || "",
        story_title: aboutResponse.story_title || "",
        story_content: aboutResponse.story_content || "",
        mission_title: aboutResponse.mission_title || "",
        mission_content: aboutResponse.mission_content || "",
        vision_title: aboutResponse.vision_title || "",
        vision_content: aboutResponse.vision_content || "",
        contact_title: aboutResponse.contact_title || "",
        contact_description: aboutResponse.contact_description || "",
        contact_address: aboutResponse.contact_address || "",
        contact_email: aboutResponse.contact_email || "",
        contact_phone: aboutResponse.contact_phone || "",
        contact_map_url: aboutResponse.contact_map_url || "",
        section_body: aboutResponse.section_body || "",
      });
    }
  };

  const handleCreateTeamMember = async () => {
    if (newTeamMember.name.trim() && newTeamMember.role.trim()) {
      try {
        await createTeamMemberMutation.mutateAsync(newTeamMember);
        setNewTeamMember({ name: "", role: "", image_url: "" });
        setIsTeamDialogOpen(false);
      } catch (error) {
        console.error("Error creating team member:", error);
      }
    }
  };

  const handleEditTeamMember = (member: TeamMember) => {
    setEditingTeamMember(member);
  };

  const handleSaveTeamMemberEdit = async () => {
    if (editingTeamMember) {
      try {
        await updateTeamMemberMutation.mutateAsync({
          id: editingTeamMember.id,
          teamMemberData: editingTeamMember,
        });
        setEditingTeamMember(null);
      } catch (error) {
        console.error("Error updating team member:", error);
      }
    }
  };

  const handleDeleteTeamMember = async (id: number) => {
    try {
      await deleteTeamMemberMutation.mutateAsync(id);
    } catch (error) {
      console.error("Error deleting team member:", error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("admin.about.title")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("admin.about.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                className="flex items-center gap-2"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateAboutMutation.isPending}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4" />
                {updateAboutMutation.isPending
                  ? t("common.saving")
                  : t("common.save")}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Edit className="w-4 h-4" />
              {t("common.edit")}
            </Button>
          )}
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-7 mb-6">
            <TabsTrigger value="hero">{t("admin.about.tabs.hero")}</TabsTrigger>
            <TabsTrigger value="section_body">
              {t("admin.about.tabs.body", "About Body")}
            </TabsTrigger>
            <TabsTrigger value="story">
              {t("admin.about.tabs.story")}
            </TabsTrigger>
            <TabsTrigger value="mission">
              {t("admin.about.tabs.mission")}
            </TabsTrigger>
            <TabsTrigger value="vision">
              {t("admin.about.tabs.vision")}
            </TabsTrigger>
            <TabsTrigger value="contact">
              {t("admin.about.tabs.contact")}
            </TabsTrigger>
            <TabsTrigger value="team">{t("admin.about.tabs.team")}</TabsTrigger>
          </TabsList>

          {/* Hero Section Tab */}
          <TabsContent value="hero" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  {t("admin.about.hero_section")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {isEditing ? (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="hero_title">
                        {t("admin.about.hero_title")}
                      </Label>
                      <Input
                        id="hero_title"
                        value={aboutData.hero_title}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            hero_title: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.hero_title_placeholder")}
                        className="text-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hero_description">
                        {t("admin.about.hero_description")}
                      </Label>
                      <Textarea
                        id="hero_description"
                        value={aboutData.hero_description}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            hero_description: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.hero_description_placeholder"
                        )}
                        rows={4}
                        className="text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="hero_image">
                        {t("admin.about.hero_image")}
                      </Label>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("admin.about.hero_image_help")}
                      </p>
                      <ImageUpload
                        label=""
                        value={aboutData.hero_image}
                        onChange={handleHeroImageChange}
                        onUpload={handleHeroImageUpload}
                        multiple={false}
                        className="mt-2"
                      />
                    </div>
                    {aboutData.hero_image && (
                      <div className="mt-4">
                        <img
                          src={aboutData.hero_image}
                          alt="Hero preview"
                          className="w-full h-64 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {aboutData.hero_image && (
                      <div className="w-full">
                        <img
                          src={aboutData.hero_image}
                          alt="Hero"
                          className="w-full h-96 object-cover rounded-lg border border-gray-200 dark:border-gray-700 shadow-md"
                        />
                      </div>
                    )}
                    <div className="space-y-3">
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {aboutData.hero_title || t("admin.about.no_hero_title")}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed">
                        {aboutData.hero_description ||
                          t("admin.about.no_hero_description")}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="section_body" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.about.tabs.body", "About Body")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <ReactQuill
                      theme="snow"
                      value={aboutData.section_body}
                      onChange={(value) =>
                        setAboutData({ ...aboutData, section_body: value })
                      }
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, false] }],
                          ["bold", "italic", "underline", "strike", "blockquote"],
                          [
                            { list: "ordered" },
                            { list: "bullet" },
                            { indent: "-1" },
                            { indent: "+1" },
                          ],
                          ["link", "image"],
                          ["clean"],
                        ],
                      }}
                      className="min-h-[300px]"
                    />
                  </div>
                ) : (
                  <div className="prose dark:prose-invert max-w-none">
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          aboutData.section_body ||
                          t("admin.about.no_body", "No content provided"),
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Story Section Tab */}
          <TabsContent value="story" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.about.story_section")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="story_title">
                        {t("admin.about.story_title")}
                      </Label>
                      <Input
                        id="story_title"
                        value={aboutData.story_title}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            story_title: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.story_title_placeholder")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="story_content">
                        {t("admin.about.story_content")}
                      </Label>
                      <Textarea
                        id="story_content"
                        value={aboutData.story_content}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            story_content: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.story_content_placeholder")}
                        rows={8}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {aboutData.story_title || t("admin.about.no_story_title")}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                      {aboutData.story_content ||
                        t("admin.about.no_story_content")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mission Section Tab */}
          <TabsContent value="mission" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.about.mission_section")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="mission_title">
                        {t("admin.about.mission_title")}
                      </Label>
                      <Input
                        id="mission_title"
                        value={aboutData.mission_title}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            mission_title: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.mission_title_placeholder")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="mission_content">
                        {t("admin.about.mission_content")}
                      </Label>
                      <Textarea
                        id="mission_content"
                        value={aboutData.mission_content}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            mission_content: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.mission_content_placeholder"
                        )}
                        rows={6}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {aboutData.mission_title ||
                        t("admin.about.no_mission_title")}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                      {aboutData.mission_content ||
                        t("admin.about.no_mission_content")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vision Section Tab */}
          <TabsContent value="vision" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.about.vision_section")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="vision_title">
                        {t("admin.about.vision_title")}
                      </Label>
                      <Input
                        id="vision_title"
                        value={aboutData.vision_title}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            vision_title: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.vision_title_placeholder")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="vision_content">
                        {t("admin.about.vision_content")}
                      </Label>
                      <Textarea
                        id="vision_content"
                        value={aboutData.vision_content}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            vision_content: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.vision_content_placeholder"
                        )}
                        rows={6}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                      {aboutData.vision_title ||
                        t("admin.about.no_vision_title")}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                      {aboutData.vision_content ||
                        t("admin.about.no_vision_content")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Section Tab */}
          <TabsContent value="contact" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t("admin.about.contact_section")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="contact_title">
                        {t("admin.about.contact_title")}
                      </Label>
                      <Input
                        id="contact_title"
                        value={aboutData.contact_title}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            contact_title: e.target.value,
                          })
                        }
                        placeholder={t("admin.about.contact_title_placeholder")}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_description">
                        {t("admin.about.contact_description")}
                      </Label>
                      <Textarea
                        id="contact_description"
                        value={aboutData.contact_description}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            contact_description: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.contact_description_placeholder"
                        )}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_address">
                        {t("admin.about.contact_address")}
                      </Label>
                      <Input
                        id="contact_address"
                        value={aboutData.contact_address}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            contact_address: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.contact_address_placeholder"
                        )}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_email">
                          {t("admin.about.contact_email")}
                        </Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={aboutData.contact_email}
                          onChange={(e) =>
                            setAboutData({
                              ...aboutData,
                              contact_email: e.target.value,
                            })
                          }
                          placeholder={t(
                            "admin.about.contact_email_placeholder"
                          )}
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">
                          {t("admin.about.contact_phone")}
                        </Label>
                        <Input
                          id="contact_phone"
                          type="tel"
                          value={aboutData.contact_phone}
                          onChange={(e) =>
                            setAboutData({
                              ...aboutData,
                              contact_phone: e.target.value,
                            })
                          }
                          placeholder={t(
                            "admin.about.contact_phone_placeholder"
                          )}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="contact_map_url">
                        {t("admin.about.contact_map_url")}
                      </Label>
                      <Textarea
                        id="contact_map_url"
                        value={aboutData.contact_map_url}
                        onChange={(e) =>
                          setAboutData({
                            ...aboutData,
                            contact_map_url: e.target.value,
                          })
                        }
                        placeholder={t(
                          "admin.about.contact_map_url_placeholder"
                        )}
                        rows={3}
                      />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t("admin.about.contact_map_url_help")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        {aboutData.contact_title ||
                          t("admin.about.no_contact_title")}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap leading-relaxed">
                        {aboutData.contact_description ||
                          t("admin.about.no_contact_description")}
                      </p>
                    </div>
                    {(aboutData.contact_address ||
                      aboutData.contact_email ||
                      aboutData.contact_phone) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          {aboutData.contact_address && (
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {t("contact.address")}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-400">
                                {aboutData.contact_address}
                              </p>
                            </div>
                          )}
                          {aboutData.contact_email && (
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {t("contact.emailUs")}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-400">
                                {aboutData.contact_email}
                              </p>
                            </div>
                          )}
                          {aboutData.contact_phone && (
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                                {t("contact.callUs")}
                              </h4>
                              <p className="text-gray-600 dark:text-gray-400">
                                {aboutData.contact_phone}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    {aboutData.contact_map_url && (
                      <div className="pt-4">
                        <iframe
                          src={aboutData.contact_map_url}
                          width="100%"
                          height="300"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          className="rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Members Tab */}
          <TabsContent value="team" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {t("admin.about.team_members")}
                  </CardTitle>
                  <Button
                    onClick={() => setIsTeamDialogOpen(true)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    {t("admin.about.add_team_member")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {teamMembersResponse?.team_members?.map((member) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="border rounded-lg p-4 space-y-3 hover:shadow-lg transition-shadow bg-white dark:bg-gray-800"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg text-gray-900 dark:text-white">
                            {member.name}
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {member.role}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTeamMember(member)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTeamMember(member.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {member.image_url ? (
                        <div className="mt-3 flex justify-center">
                          <img
                            src={member.image_url}
                            alt={member.name}
                            className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 shadow-md"
                          />
                        </div>
                      ) : (
                        <div className="mt-3 flex justify-center">
                          <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                            <User className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                  {(!teamMembersResponse?.team_members ||
                    teamMembersResponse.team_members.length === 0) && (
                      <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                        {t("admin.about.team_members")} - No team members yet
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Status Card */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.about.status")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {t("admin.about.lastUpdated")}:{" "}
                {aboutResponse?.updated_at
                  ? new Date(aboutResponse.updated_at).toLocaleDateString(
                    currentLanguage
                  )
                  : new Date().toLocaleDateString(currentLanguage)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Create Team Member Dialog */}
      <Dialog open={isTeamDialogOpen} onOpenChange={setIsTeamDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.about.add_team_member")}</DialogTitle>
            <DialogDescription>
              {t("admin.about.add_team_member_description")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="member_name">
                {t("admin.about.member_name")}
              </Label>
              <Input
                id="member_name"
                value={newTeamMember.name}
                onChange={(e) =>
                  setNewTeamMember({ ...newTeamMember, name: e.target.value })
                }
                placeholder={t("admin.about.member_name_placeholder")}
              />
            </div>
            <div>
              <Label htmlFor="member_role">
                {t("admin.about.member_role")}
              </Label>
              <Input
                id="member_role"
                value={newTeamMember.role}
                onChange={(e) =>
                  setNewTeamMember({ ...newTeamMember, role: e.target.value })
                }
                placeholder={t("admin.about.member_role_placeholder")}
              />
            </div>
            <div>
              <Label htmlFor="member_image">
                {t("admin.about.member_image")}
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {t("admin.about.member_image_help")}
              </p>
              <ImageUpload
                label=""
                value={newTeamMember.image_url}
                onChange={handleNewTeamMemberImageChange}
                onUpload={handleTeamMemberImageUpload}
                multiple={false}
              />
              {newTeamMember.image_url && (
                <div className="mt-3">
                  <img
                    src={newTeamMember.image_url}
                    alt="Preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsTeamDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCreateTeamMember}
              disabled={
                !newTeamMember.name.trim() ||
                !newTeamMember.role.trim() ||
                createTeamMemberMutation.isPending
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createTeamMemberMutation.isPending
                ? t("common.creating")
                : t("admin.about.create_member")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Dialog */}
      {editingTeamMember && (
        <Dialog
          open={!!editingTeamMember}
          onOpenChange={() => setEditingTeamMember(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.about.edit_team_member")}</DialogTitle>
              <DialogDescription>
                {t("admin.about.edit_team_member_description")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit_member_name">
                  {t("admin.about.member_name")}
                </Label>
                <Input
                  id="edit_member_name"
                  value={editingTeamMember.name}
                  onChange={(e) =>
                    setEditingTeamMember({
                      ...editingTeamMember,
                      name: e.target.value,
                    })
                  }
                  placeholder={t("admin.about.member_name_placeholder")}
                />
              </div>
              <div>
                <Label htmlFor="edit_member_role">
                  {t("admin.about.member_role")}
                </Label>
                <Input
                  id="edit_member_role"
                  value={editingTeamMember.role}
                  onChange={(e) =>
                    setEditingTeamMember({
                      ...editingTeamMember,
                      role: e.target.value,
                    })
                  }
                  placeholder={t("admin.about.member_role_placeholder")}
                />
              </div>
              <div>
                <Label htmlFor="edit_member_image">
                  {t("admin.about.member_image")}
                </Label>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {t("admin.about.member_image_help")}
                </p>
                <ImageUpload
                  label=""
                  value={editingTeamMember.image_url || ""}
                  onChange={handleEditTeamMemberImageChange}
                  onUpload={handleTeamMemberImageUpload}
                  multiple={false}
                />
                {editingTeamMember.image_url && (
                  <div className="mt-3">
                    <img
                      src={editingTeamMember.image_url}
                      alt="Preview"
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingTeamMember(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSaveTeamMemberEdit}
                disabled={
                  !editingTeamMember.name.trim() ||
                  !editingTeamMember.role.trim() ||
                  updateTeamMemberMutation.isPending
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                {updateTeamMemberMutation.isPending
                  ? t("common.saving")
                  : t("common.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

About.displayName = "About";

export default About;
